import { getActiveSpaceId, getActiveSpaceName, getActiveSpaceSlug } from './backendConfig.js';

/**
 * The "Space" field — the space that owns an item. One name at every layer, like Created
 * (utils/createdDate.js) and Approval (utils/approvalField.js):
 *
 *   view configs persist  '__space'                 (a group field, a filter's columnId, a body field)
 *   standard sort id      'space'                   (the Table column's id, shared by every sort)
 *   display name          "Space"
 *   read it with          owningSpaceName(node)
 *   guard a field ref     isSpaceField(ref)
 *
 * COMPUTED and READ-ONLY. The value is `items.primary_space_id`, carried on the node as `spaceId`
 * and named here. Nothing writes it through a field: moving an item to another space is ownership
 * transfer, which needs its own preview of who loses access (cross-space sharing plan, Phase 6).
 *
 * The key keeps its double underscore because saved group-bys already persist it. It is not a real
 * field name, so it can never collide with a space's own field called "Space".
 */
export const SPACE_FIELD_KEY = '__space';
export const SPACE_FIELD_LABEL = 'Space';
/** The id the standard sort lists persist, and the Table column's id. */
export const SPACE_SORT_ID = 'space';

// Synthetic like Created, and read-only, so dropping a card into another space's column never
// tries to write it. A single select so the filter offers the spaces on screen as its values.
export const SPACE_FIELD_DEF = Object.freeze({
  id: SPACE_FIELD_KEY,
  name: SPACE_FIELD_KEY,
  label: SPACE_FIELD_LABEL,
  type: 'singleSelect',
  source: 'internal',
  synthetic: true,
  readOnly: true,
});

/**
 * Column-shaped, for the Table/List inline filter row (see APPROVAL_FILTER_COLUMN), on a surface
 * whose columns do not include the Table's own Space column.
 */
export const SPACE_FILTER_COLUMN = Object.freeze({
  id: SPACE_FIELD_KEY,
  label: SPACE_FIELD_LABEL,
  type: 'singleLineText',
  readOnly: true,
  accessor: (row) => owningSpaceName(row),
});

export const isSpaceField = (ref) => ref === SPACE_FIELD_KEY || ref?.id === SPACE_FIELD_KEY;

// Space id → name, for items owned by a space other than the open one. Filled from two places: an
// Inclusion's `includedFrom` (which carries its owner's name), and the reader's own spaces when a
// cross-space collection hydrates items from elsewhere (data/foreignItems.js).
const _names = new Map();

/** Remember the names of spaces, from `{ id, name }` rows. Rows without both are skipped. */
export function rememberSpaceNames(rows) {
  for (const r of rows || []) if (r?.id && r?.name) _names.set(r.id, r.name);
}

/** True when the name of this space id is already known. */
export const knowsSpaceName = (id) => _names.has(id);

// The open space's id as resolved from its slug. `getActiveSpaceId()` is null on an ordinary page
// load (the workspace meta does not carry the uuid — see resolveSpaceId in SupabaseDataService), and
// with no id to compare against, every item a cross-space collection pulled in was named after the
// open space. Kept per slug so a stale id never outlives a space switch.
let _resolved = { slug: null, id: null };

/** Remember the id the open space's slug resolved to. */
export function rememberActiveSpaceId(slug, id) {
  if (slug && id) _resolved = { slug, id };
}

/** The open space's id: the explicit one when the meta carried it, else the one resolved from its slug. */
export function knownActiveSpaceId() {
  return getActiveSpaceId() || (_resolved.slug && _resolved.slug === getActiveSpaceSlug() ? _resolved.id : null);
}

/**
 * The name of the space an item belongs to.
 *
 * Every row gets one, the space's own items included, so grouping and sorting by Space make one
 * bucket per space rather than lumping everything owned under "(empty)".
 *
 * Read in this order: an Inclusion's owner (`includedFrom`); then the item's own space id, when it
 * is not the open space; then the open space. Reading `includedFrom` alone mislabelled every item a
 * cross-space collection pulled in, since those are fetched by id and are not Inclusions.
 *
 * @param {{ spaceId?: string|null, includedFrom?: { spaceId?: string|null, name?: string|null }|null }} node
 * @param {string|null} [activeSpaceName] defaults to the active space's name
 * @param {string|null} [activeSpaceId]   defaults to the active space's id
 * @returns {string}
 */
export function owningSpaceName(node, activeSpaceName = getActiveSpaceName(), activeSpaceId = knownActiveSpaceId()) {
  if (node?.includedFrom) {
    return node.includedFrom.name || _names.get(node.includedFrom.spaceId) || 'Another space';
  }
  const id = node?.spaceId || null;
  if (id && activeSpaceId && id !== activeSpaceId) return _names.get(id) || 'Another space';
  return activeSpaceName || 'This space';
}

/**
 * Compare two nodes by owning space name, A to Z, then by title. Most of a view is usually one space,
 * so the title is what orders the rows inside each space instead of an arbitrary tie-break.
 */
export const compareOwningSpace = (a, b) => owningSpaceName(a).localeCompare(owningSpaceName(b))
  || (a?.title || '').localeCompare(b?.title || '');
